import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Asset } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { useScale } from "common/utils";
import { AssetsResponse } from "features/user/api";
import { actions } from "features/courses/coursesSlice";
import { updateAssetUsage } from "features/subscription";
import { updateCourseConfig } from "features/courses/api";

export const useOpenCv = ({ setInitialized }: { setInitialized?: () => void }) => {
  useEffect(() => {
    if ("cv" in window) {
      if (setInitialized) {
        setInitialized();
      }
      console.warn("already import opencv!");
    } else {
      const script = document.createElement("script");
      script.src = "https://assets.kids.inexus-co.com/lib/opencv_4.10.0.js";
      script.async = true;
      script.onload = () => {
        // @ts-expect-error cv (OpenCV) is a runtime global without type definitions
        cv["onRuntimeInitialized"] = () => {
          console.warn("OpenCV is ready to use.");
          if (setInitialized) {
            setInitialized();
          }
        };
      };
      script.onerror = () => {
        console.error("Failed to load OpenCV.js");
      };
      document.head.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useCamera = (videoRef: React.MutableRefObject<HTMLVideoElement>) => {
  const { scale } = useScale();
  const dispatch = useDispatch();
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [clicked, setClicked] = useState(false);
  const [cameraIds, setCameraIds] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);
  const cameraDeviceId = useSelector((state: RootState) => state.courses.config.tangible.deviceId);
  const parent = useSelector((state: RootState) => state.user.parent);

  const handleOpenCamera = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (deviceId: string, success?: () => void, error?: (err: any) => void) => {
      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: {
            deviceId: { exact: deviceId },
            aspectRatio: 16 / 9,
            width: { ideal: 1920 },
            frameRate: 30,
          },
        })
        .then((stream) => {
          let recordedChunks = [];

          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: "video/webm; codecs=vp9",
          });

          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              recordedChunks.push(event.data);
            }
          };

          mediaRecorder.onstop = async () => {
            const blob = new Blob(recordedChunks, { type: "video/webm" });

            const videoURL = URL.createObjectURL(blob);
            const downloadLink = document.createElement("a");
            downloadLink.href = videoURL;
            downloadLink.download = "recorded_video.webm"; // 设置下载的文件名
            document.body.appendChild(downloadLink);
            downloadLink.click(); // 触发下载
            document.body.removeChild(downloadLink); // 移除下载链接

            recordedChunks = [];
          };

          // mediaRecorder.start();

          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = function () {
              videoRef.current.play();
              videoRef.current.width = videoRef.current.videoWidth; // width, heightを設定しないとcap.read(src)で失敗する。
              videoRef.current.height = videoRef.current.videoHeight;
              if (success) {
                success();
              }
            };
          } else {
            error("Can not found video!");
          }
        })
        .catch(error);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const initCamera = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (success?: () => void, error?: (err: any) => void) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true,
        });
        stream.getVideoTracks().forEach((track) => track.stop());
        const devices = await navigator.mediaDevices.enumerateDevices();
        const deviceIds = devices
          .filter((device) => device.kind === "videoinput")
          .map((device) => device.deviceId);
        setCameraIds(deviceIds);
        let deviceId: string;
        if (cameraDeviceId) {
          const deviceIdIndex = deviceIds.indexOf(cameraDeviceId);
          if (deviceIdIndex !== -1) {
            setCameraIndex(deviceIdIndex);
            deviceId = cameraDeviceId;
          } else {
            setCameraIndex(deviceIds.length - 1);
            deviceId = deviceIds[deviceIds.length - 1];
            dispatch(actions.updateCameraDeviceId(deviceId));
            dispatch(
              updateCourseConfig({
                mainUid: parent.uid,
                type: "tangible",
              }),
            );
          }
        } else {
          setCameraIndex(deviceIds.length - 1);
          deviceId = deviceIds[deviceIds.length - 1];
          dispatch(actions.updateCameraDeviceId(deviceId));
          dispatch(
            updateCourseConfig({
              mainUid: parent.uid,
              type: "tangible",
            }),
          );
        }
        handleOpenCamera(deviceId, success, error);
      } catch (err) {
        error(err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cameraDeviceId],
  );

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      if (videoRef.current?.srcObject) {
        const tracks = (videoRef.current.srcObject as MediaStream).getVideoTracks();
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          track.stop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        videoRef.current.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getVideoTracks();
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        track.stop();
      }
      videoRef.current.srcObject = null;
      setTimeout(() => handleOpenCamera(cameraIds[cameraIndex]), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  const switchCamera = () => {
    if (clicked) return;
    setClicked(true);
    if (cameraIndex === cameraIds.length - 1) {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream)
          .getVideoTracks()
          .forEach((track) => track.stop());
        videoRef.current.srcObject = null;
        setTimeout(
          () =>
            handleOpenCamera(
              cameraIds[0],
              () => {
                setClicked(false);
              },
              () => {
                setClicked(false);
              },
            ),
          200,
        );
        setCameraIndex(0);
        dispatch(actions.updateCameraDeviceId(cameraIds[0]));
        dispatch(
          updateCourseConfig({
            mainUid: parent.uid,
            type: "tangible",
          }),
        );
      }
    } else {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream)
          .getVideoTracks()
          .forEach((track) => track.stop());
        videoRef.current.srcObject = null;
        setTimeout(
          () =>
            handleOpenCamera(
              cameraIds[cameraIndex + 1],
              () => {
                setClicked(false);
              },
              () => {
                setClicked(false);
              },
            ),
          200,
        );
        setCameraIndex(cameraIndex + 1);
        dispatch(actions.updateCameraDeviceId(cameraIds[cameraIndex + 1]));
        dispatch(
          updateCourseConfig({
            mainUid: parent.uid,
            type: "tangible",
          }),
        );
      }
    }
  };

  const resetCamera = (success: () => void, error?: () => void) => {
    if (clicked) return;
    setClicked(true);
    handleOpenCamera(
      cameraIds[cameraIndex],
      () => {
        setClicked(false);
        success();
      },
      () => {
        setClicked(false);
        if (error) {
          error();
        }
      },
    );
  };

  const cancel = () => {
    try {
      const tracks = (videoRef.current.srcObject as MediaStream).getVideoTracks();
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        track.stop();
      }
      videoRef.current.srcObject = null;
      mediaRecorderRef.current.stop();
    } catch (error) {
      console.error(error);
    }
  };

  return { initCamera, switchCamera, resetCamera, cancel };
};

const ASSET_QUERY_KEY = "creator/getUserAssets";
const PARENT_ASSET_QUERY_KEY = "parent/getUserAssets";

/**
 * Asset キャッシュ・使用量管理 hook
 */
export const useAssetCache = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  /** Asset 保存後のキャッシュ・limit 更新 */
  const onAssetSaved = useCallback(
    (uid: string, asset: Asset) => {
      queryClient.setQueryData<AssetsResponse>([ASSET_QUERY_KEY, uid], (old) => ({
        assets: [asset, ...(old?.assets || [])],
        totalSize: (old?.totalSize || 0) + (asset.file_size || 0),
      }));
      dispatch(updateAssetUsage({ uid, delta: asset.file_size || 0 }));
    },
    [dispatch, queryClient],
  );

  /** Asset 削除後のキャッシュ・limit 更新 */
  const onAssetDeleted = useCallback(
    (uid: string, assetId: number, fileSize: number) => {
      queryClient.setQueryData<AssetsResponse>([ASSET_QUERY_KEY, uid], (old) => ({
        assets: (old?.assets || []).filter((a) => a.id !== assetId),
        totalSize: Math.max(0, (old?.totalSize || 0) - fileSize),
      }));
      dispatch(updateAssetUsage({ uid, delta: -fileSize }));
    },
    [dispatch, queryClient],
  );

  /** Asset メタデータ更新後のキャッシュ更新（limit変更なし） */
  const onAssetUpdated = useCallback(
    (uid: string, assetId: number, updates: Partial<Pick<Asset, "name" | "tags" | "metadata">>) => {
      queryClient.setQueryData<AssetsResponse>([ASSET_QUERY_KEY, uid], (old) => {
        if (!old) return old;
        return {
          ...old,
          assets: old.assets.map((asset) =>
            asset.id === assetId ? { ...asset, ...updates } : asset,
          ),
        };
      });
    },
    [queryClient],
  );

  return { onAssetSaved, onAssetDeleted, onAssetUpdated };
};

/**
 * 保護者用 Asset キャッシュ・使用量管理 hook
 * 保護者用のクエリキー（"parent/getUserAssets"）を使用
 */
export const useParentAssetCache = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  /** Asset 保存後のキャッシュ・limit 更新 */
  const onParentAssetSaved = useCallback(
    (uid: string, asset: Asset) => {
      queryClient.setQueryData<AssetsResponse>([PARENT_ASSET_QUERY_KEY, uid], (old) => ({
        assets: [asset, ...(old?.assets || [])],
        totalSize: (old?.totalSize || 0) + (asset.file_size || 0),
      }));
      // accountLimits.assets のみ更新
      // playerLimits[uid] は保護者UIDでは存在しないため自動的にスキップされる
      dispatch(updateAssetUsage({ uid, delta: asset.file_size || 0 }));
    },
    [dispatch, queryClient],
  );

  /** Asset 削除後のキャッシュ・limit 更新 */
  const onParentAssetDeleted = useCallback(
    (uid: string, assetId: number, fileSize: number) => {
      queryClient.setQueryData<AssetsResponse>([PARENT_ASSET_QUERY_KEY, uid], (old) => ({
        assets: (old?.assets || []).filter((a) => a.id !== assetId),
        totalSize: Math.max(0, (old?.totalSize || 0) - fileSize),
      }));
      dispatch(updateAssetUsage({ uid, delta: -fileSize }));
    },
    [dispatch, queryClient],
  );

  /** Asset メタデータ更新後のキャッシュ更新（limit変更なし） */
  const onParentAssetUpdated = useCallback(
    (uid: string, assetId: number, updates: Partial<Pick<Asset, "name" | "tags" | "metadata">>) => {
      queryClient.setQueryData<AssetsResponse>([PARENT_ASSET_QUERY_KEY, uid], (old) => {
        if (!old) return old;
        return {
          ...old,
          assets: old.assets.map((asset) =>
            asset.id === assetId ? { ...asset, ...updates } : asset,
          ),
        };
      });
    },
    [queryClient],
  );

  return { onParentAssetSaved, onParentAssetDeleted, onParentAssetUpdated };
};
